/**
 * Settings page component
 */

import { useState, useEffect } from 'react';
import { Button, Modal } from '../components/common';
import { BottleImport } from '../components/bottles';
import { DistilleryImport } from '../components/distilleries';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase';
import { useToast } from '../context/ToastContext';
import { exportToExcel } from '../services/excelExportService';
import { usePreferences, useUpdatePreferences } from '../hooks/usePreferences';
import { useMyViewers, useShareCollection, useRevokeShare } from '../hooks/useSharing';
import { useAdminUsers, useCreateUser, useUpdateUser, useSetUserActive } from '../hooks/useAdmin';
import { useMediaQuery } from '../hooks/useMediaQuery';
import './Utilities.css';
import './UserManagement.css';

const ROLE_LABELS = { admin: 'Admin', user: 'User' };

function UserFormModal({ isOpen, onClose, editUser, isLastAdmin }) {
  const isEditing = !!editUser;
  const { addToast } = useToast();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();

  const [email, setEmail] = useState(editUser?.email || '');
  const [displayName, setDisplayName] = useState(editUser?.display_name || '');
  const [role, setRole] = useState(editUser?.role || 'user');
  const roleChangeLocked = isEditing && editUser?.role === 'admin' && isLastAdmin;

  const isPending = createUser.isPending || updateUser.isPending;

  const handleClose = () => {
    setEmail('');
    setDisplayName('');
    setRole('user');
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await updateUser.mutateAsync({
          userId: editUser.user_id,
          email: email !== editUser.email ? email : undefined,
          displayName,
          role,
        });
        addToast({ type: 'success', message: 'User updated successfully' });
      } else {
        await createUser.mutateAsync({ email, displayName, role });
        addToast({ type: 'success', message: `User ${email} created. A welcome email has been sent.` });
      }
      handleClose();
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Operation failed' });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={isEditing ? 'Edit User' : 'Add User'}>
      <form onSubmit={handleSubmit} className="user-form">
        <div className="user-form__field">
          <label htmlFor="uf-email">Email</label>
          <input id="uf-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off" />
        </div>
        <div className="user-form__field">
          <label htmlFor="uf-display-name">Display Name</label>
          <input id="uf-display-name" type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={50} placeholder="Optional" />
        </div>
        <div className="user-form__field">
          <label htmlFor="uf-role">Role</label>
          <select
            id="uf-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            disabled={roleChangeLocked}
            title={roleChangeLocked ? 'Cannot change role — this is the last active admin' : undefined}
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
          {roleChangeLocked && (
            <span className="user-form__field-hint">Last active admin — role cannot be changed</span>
          )}
        </div>
        <div className="user-form__actions">
          <Button variant="ghost" type="button" onClick={handleClose}>Cancel</Button>
          <Button type="submit" loading={isPending}>{isEditing ? 'Save Changes' : 'Create User'}</Button>
        </div>
      </form>
    </Modal>
  );
}

const BASE_TABS = ['Preferences', 'Collection Sharing', 'Account Settings', 'Import/Export'];

/**
 * Settings page with tabbed sections
 * @returns {React.ReactElement} Page element
 */
export default function Utilities() {
  const { user, isAdmin } = useAuth();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState('Preferences');

  const TABS = isAdmin ? [...BASE_TABS, 'User Management'] : BASE_TABS;

  const [showBottleImport, setShowBottleImport] = useState(false);
  const [showDistilleryImport, setShowDistilleryImport] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareBottles, setShareBottles] = useState(true);
  const [shareHunt, setShareHunt] = useState(true);

  // User management (admin only)
  const { data: adminUsers = [], isLoading: adminUsersLoading, error: adminUsersError } = useAdminUsers();
  const setUserActive = useSetUserActive();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const activeAdminCount = adminUsers.filter(u => u.role === 'admin' && u.is_active).length;
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);

  const handleToggleActive = async (u) => {
    try {
      await setUserActive.mutateAsync({ userId: u.user_id, isActive: !u.is_active });
      addToast({ type: 'success', message: `${u.email} has been ${u.is_active ? 'disabled' : 'enabled'}` });
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to update user status' });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const { data: preferences } = usePreferences();
  const { data: viewers = [] } = useMyViewers();
  const shareCollection = useShareCollection();
  const revokeShare = useRevokeShare();
  const updatePreferences = useUpdatePreferences();
  const [displayName, setDisplayName] = useState(user?.user_metadata?.display_name || '');
  const [averageBottleCost, setAverageBottleCost] = useState('');

  const handlePasswordReset = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      addToast({ type: 'error', message: 'New passwords do not match' });
      return;
    }

    if (newPassword.length < 6) {
      addToast({ type: 'error', message: 'Password must be at least 6 characters' });
      return;
    }

    setPasswordLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword
      });

      if (signInError) {
        addToast({ type: 'error', message: 'Current password is incorrect' });
        setPasswordLoading(false);
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      addToast({ type: 'success', message: 'Password updated successfully' });
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      addToast({ type: 'error', message: error.message || 'Failed to update password' });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  useEffect(() => {
    if (preferences?.average_bottle_cost != null) {
      setAverageBottleCost(String(preferences.average_bottle_cost));
    }
  }, [preferences]);

  const handleSavePreferences = async (e) => {
    e.preventDefault();
    const cost = averageBottleCost.trim() === '' ? null : parseFloat(averageBottleCost);
    if (cost !== null && (isNaN(cost) || cost < 0)) {
      addToast({ type: 'error', message: 'Please enter a valid cost (greater than 0)' });
      return;
    }
    try {
      await updatePreferences.mutateAsync({ average_bottle_cost: cost === 0 ? null : cost });
      addToast({ type: 'success', message: 'Preferences saved' });
    } catch (error) {
      addToast({ type: 'error', message: error.message || 'Failed to save preferences' });
    }
  };

  const handleSaveDisplayName = async (e) => {
    e.preventDefault();
    if (displayName.length > 50) {
      addToast({ type: 'error', message: 'Display name must be 50 characters or fewer' });
      return;
    }
    try {
      await supabase.auth.updateUser({ data: { display_name: displayName.trim() || null } });
      addToast({ type: 'success', message: 'Display name saved' });
    } catch (error) {
      addToast({ type: 'error', message: error.message || 'Failed to save display name' });
    }
  };

  const handleShareSubmit = async (e) => {
    e.preventDefault();
    if (!shareEmail.trim()) return;
    if (!shareBottles && !shareHunt) {
      addToast({ type: 'error', message: 'Select at least one section to share' });
      return;
    }
    try {
      await shareCollection.mutateAsync({ email: shareEmail.trim(), shareBottles, shareHunt });
      addToast({ type: 'success', message: `Collection shared with ${shareEmail.trim()}` });
      setShareEmail('');
      setShareBottles(true);
      setShareHunt(true);
    } catch (error) {
      addToast({ type: 'error', message: error.message || 'Failed to share collection' });
    }
  };

  const handleRevokeShare = async (shareId, email) => {
    try {
      await revokeShare.mutateAsync(shareId);
      addToast({ type: 'success', message: `Access revoked for ${email}` });
    } catch (error) {
      addToast({ type: 'error', message: error.message || 'Failed to revoke access' });
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      await exportToExcel();
      addToast({ type: 'success', message: 'Export completed successfully' });
    } catch (error) {
      addToast({ type: 'error', message: error.message || 'Failed to export data' });
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="utilities-page">
      <div className="utilities-page__header">
        <h1 className="utilities-page__title">Settings</h1>
      </div>

      <div className="utilities-tabs">
        {TABS.map(tab => (
          <button
            key={tab}
            className={`utilities-tab${activeTab === tab ? ' utilities-tab--active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="utilities-page__content">

        {activeTab === 'Preferences' && (
          <section className="utilities-section">
            <form onSubmit={handleSavePreferences} className="preferences-form">
              <div className="preferences-form__field">
                <label htmlFor="average-bottle-cost">Average Bottle Cost</label>
                <p className="preferences-form__hint">
                  Used as a default reference when estimating collection value.
                </p>
                <div className="preferences-form__input-group">
                  <span className="preferences-form__currency">$</span>
                  <input
                    id="average-bottle-cost"
                    type="number"
                    min="0"
                    step="0.01"
                    value={averageBottleCost}
                    onChange={(e) => setAverageBottleCost(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="preferences-form__actions">
                <Button type="submit" loading={updatePreferences.isPending}>
                  Save Preferences
                </Button>
              </div>
            </form>
          </section>
        )}

        {activeTab === 'Collection Sharing' && (
          <section className="utilities-section">
            <p className="utilities-section__description">
              Grant other users view-only access to your collection. Choose which sections to share.
            </p>

            <form onSubmit={handleShareSubmit} className="sharing-form">
              <div className="sharing-form__row">
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  className="sharing-form__input"
                  required
                />
                <Button type="submit" loading={shareCollection.isPending}>
                  Share
                </Button>
              </div>
              <div className="sharing-form__checkboxes">
                <label className="sharing-form__checkbox-label">
                  <input
                    type="checkbox"
                    checked={shareBottles}
                    onChange={(e) => setShareBottles(e.target.checked)}
                  />
                  Bottles
                </label>
                <label className="sharing-form__checkbox-label">
                  <input
                    type="checkbox"
                    checked={shareHunt}
                    onChange={(e) => setShareHunt(e.target.checked)}
                  />
                  Hunt List
                </label>
              </div>
            </form>

            {viewers.length > 0 && (
              <ul className="sharing-viewer-list">
                {viewers.map((v) => (
                  <li key={v.share_id} className="sharing-viewer-list__item">
                    <span className="sharing-viewer-list__identity">
                      {v.viewer_display_name && (
                        <span className="sharing-viewer-list__display-name">{v.viewer_display_name}</span>
                      )}
                      <span className="sharing-viewer-list__email">{v.viewer_email}</span>
                      <span className="sharing-viewer-list__permissions">
                        {v.share_bottles && <span className="sharing-viewer-list__perm">Bottles</span>}
                        {v.share_hunt && <span className="sharing-viewer-list__perm">Hunt List</span>}
                      </span>
                    </span>
                    <button
                      className="sharing-viewer-list__revoke"
                      onClick={() => handleRevokeShare(v.share_id, v.viewer_email)}
                      disabled={revokeShare.isPending}
                      title="Revoke access"
                      aria-label={`Revoke access for ${v.viewer_email}`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                      Revoke
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {activeTab === 'Account Settings' && (
          <section className="utilities-section">
            <form onSubmit={handleSaveDisplayName} className="preferences-form">
              <div className="preferences-form__field">
                <label htmlFor="display-name">Display Name</label>
                <p className="preferences-form__hint">
                  Shown to others when you share your collection. Defaults to your email if blank.
                </p>
                <input
                  id="display-name"
                  type="text"
                  maxLength={50}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="First Last"
                />
              </div>
              <div className="preferences-form__actions">
                <Button type="submit">Save Display Name</Button>
              </div>
            </form>
            <hr className="utilities-section__divider" />
            <div className="utilities-section__actions">
              <Button onClick={() => setShowPasswordModal(true)}>
                Change Password
              </Button>
            </div>
          </section>
        )}

        {activeTab === 'Import/Export' && (
          <section className="utilities-section">
            <h2 className="utilities-section__title">Data Export</h2>
            <p className="utilities-section__description">
              Export your entire collection to an Excel spreadsheet with separate sheets for bottles and distilleries.
            </p>
            <div className="utilities-section__actions">
              <Button onClick={handleExport} loading={exportLoading}>
                Export to Excel
              </Button>
            </div>

            <hr className="utilities-section__divider" />
            <h2 className="utilities-section__title">Data Import</h2>
            <p className="utilities-section__description">
              Import bottles and distilleries from CSV files.
            </p>
            <div className="utilities-section__actions">
              <Button onClick={() => setShowBottleImport(true)}>
                Import Bottles
              </Button>
              <Button onClick={() => setShowDistilleryImport(true)}>
                Import Distilleries
              </Button>
            </div>
          </section>
        )}

        {activeTab === 'User Management' && isAdmin && (
          <section className="utilities-section utilities-section--wide">
            <div className="user-mgmt-page__header">
              <div />
              <Button onClick={() => { setEditingUser(null); setShowUserModal(true); }}>Add User</Button>
            </div>

            {adminUsersLoading && <div className="user-mgmt-page__loading">Loading users…</div>}
            {adminUsersError && <div className="user-mgmt-page__error">Failed to load users: {adminUsersError.message}</div>}

            {!adminUsersLoading && !adminUsersError && (
              isMobile ? (
                <div className="user-mgmt-cards">
                  {adminUsers.length === 0 && <p className="user-mgmt-page__empty">No users found.</p>}
                  {adminUsers.map((u) => {
                    const isCurrentUser = u.user_id === user?.id;
                    const isLastAdmin = u.role === 'admin' && u.is_active && activeAdminCount <= 1;
                    const canDisable = !isCurrentUser && !isLastAdmin;
                    return (
                      <div key={u.user_id} className={`user-mgmt-card user-mgmt-card--clickable${!u.is_active ? ' user-mgmt-card--disabled' : ''}`} onClick={() => setViewingUser(u)}>
                        <div className="user-mgmt-card__header">
                          <span className="user-mgmt-card__email">{u.email}</span>
                          <div className="user-mgmt-card__badges">
                            <span className={`user-mgmt-badge user-mgmt-badge--${u.role}`}>{ROLE_LABELS[u.role] || u.role}</span>
                            <span className={`user-mgmt-badge user-mgmt-badge--${u.is_active ? 'active' : 'inactive'}`}>{u.is_active ? 'Active' : 'Disabled'}</span>
                          </div>
                        </div>
                        {u.display_name && <div className="user-mgmt-card__name">{u.display_name}</div>}
                        <div className="user-mgmt-card__actions">
                          <button className="user-mgmt-table__action-btn" onClick={(e) => { e.stopPropagation(); setEditingUser(u); setShowUserModal(true); }}>Edit</button>
                          {canDisable && (
                            <button
                              className={`user-mgmt-table__action-btn user-mgmt-table__action-btn--${u.is_active ? 'disable' : 'enable'}`}
                              onClick={(e) => { e.stopPropagation(); handleToggleActive(u); }}
                              disabled={setUserActive.isPending}
                            >
                              {u.is_active ? 'Disable' : 'Enable'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="user-mgmt-table-wrap">
                  <table className="user-mgmt-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Display Name</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminUsers.map((u) => {
                        const isCurrentUser = u.user_id === user?.id;
                        const isLastAdmin = u.role === 'admin' && u.is_active && activeAdminCount <= 1;
                        const canDisable = !isCurrentUser && !isLastAdmin;
                        return (
                          <tr
                            key={u.user_id}
                            className={`user-mgmt-table__row--clickable${!u.is_active ? ' user-mgmt-table__row--disabled' : ''}`}
                            onClick={() => setViewingUser(u)}
                          >
                            <td className="user-mgmt-table__email">{u.email}</td>
                            <td>{u.display_name || <span className="user-mgmt-table__empty">—</span>}</td>
                            <td><span className={`user-mgmt-badge user-mgmt-badge--${u.role}`}>{ROLE_LABELS[u.role] || u.role}</span></td>
                            <td><span className={`user-mgmt-badge user-mgmt-badge--${u.is_active ? 'active' : 'inactive'}`}>{u.is_active ? 'Active' : 'Disabled'}</span></td>
                            <td className="user-mgmt-table__actions" onClick={(e) => e.stopPropagation()}>
                              <button className="user-mgmt-table__action-btn" onClick={() => { setEditingUser(u); setShowUserModal(true); }} title="Edit user">Edit</button>
                              {canDisable && (
                                <button
                                  className={`user-mgmt-table__action-btn user-mgmt-table__action-btn--${u.is_active ? 'disable' : 'enable'}`}
                                  onClick={() => handleToggleActive(u)}
                                  disabled={setUserActive.isPending}
                                  title={u.is_active ? 'Disable user' : 'Enable user'}
                                >
                                  {u.is_active ? 'Disable' : 'Enable'}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {adminUsers.length === 0 && <p className="user-mgmt-page__empty">No users found.</p>}
                </div>
              )
            )}
          </section>
        )}

      </div>

      <Modal isOpen={!!viewingUser} onClose={() => setViewingUser(null)} title="User Details">
        {viewingUser && (
          <div className="user-view">
            <div className="user-view__row">
              <span className="user-view__label">Email</span>
              <span className="user-view__value">{viewingUser.email}</span>
            </div>
            <div className="user-view__row">
              <span className="user-view__label">Display Name</span>
              <span className="user-view__value">{viewingUser.display_name || <span className="user-mgmt-table__empty">—</span>}</span>
            </div>
            <div className="user-view__row">
              <span className="user-view__label">Role</span>
              <span className="user-view__value">
                <span className={`user-mgmt-badge user-mgmt-badge--${viewingUser.role}`}>{ROLE_LABELS[viewingUser.role] || viewingUser.role}</span>
              </span>
            </div>
            <div className="user-view__row">
              <span className="user-view__label">Status</span>
              <span className="user-view__value">
                <span className={`user-mgmt-badge user-mgmt-badge--${viewingUser.is_active ? 'active' : 'inactive'}`}>{viewingUser.is_active ? 'Active' : 'Disabled'}</span>
              </span>
            </div>
            <div className="user-view__row">
              <span className="user-view__label">Joined</span>
              <span className="user-view__value user-view__value--mono">{formatDate(viewingUser.created_at)}</span>
            </div>
            <div className="user-view__row">
              <span className="user-view__label">Last Sign In</span>
              <span className="user-view__value user-view__value--mono">{formatDate(viewingUser.last_sign_in_at)}</span>
            </div>
            <div className="user-view__actions">
              <Button variant="ghost" onClick={() => setViewingUser(null)}>Close</Button>
              <Button onClick={() => { setEditingUser(viewingUser); setViewingUser(null); setShowUserModal(true); }}>Edit</Button>
            </div>
          </div>
        )}
      </Modal>

      <UserFormModal
        key={editingUser?.user_id ?? 'new'}
        isOpen={showUserModal}
        onClose={() => { setShowUserModal(false); setEditingUser(null); }}
        editUser={editingUser}
        isLastAdmin={editingUser?.role === 'admin' && editingUser?.is_active && activeAdminCount <= 1}
      />

      <BottleImport
        isOpen={showBottleImport}
        onClose={() => setShowBottleImport(false)}
      />

      <DistilleryImport
        isOpen={showDistilleryImport}
        onClose={() => setShowDistilleryImport(false)}
      />

      <Modal
        isOpen={showPasswordModal}
        onClose={handleClosePasswordModal}
        title="Change Password"
      >
        <form onSubmit={handlePasswordReset} className="password-form">
          <div className="password-form__field">
            <label htmlFor="current-password">Current Password</label>
            <input
              id="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          <div className="password-form__field">
            <label htmlFor="new-password">New Password</label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
            />
          </div>

          <div className="password-form__field">
            <label htmlFor="confirm-password">Confirm New Password</label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
            />
          </div>

          <div className="password-form__actions">
            <Button variant="ghost" type="button" onClick={handleClosePasswordModal}>
              Cancel
            </Button>
            <Button type="submit" loading={passwordLoading}>
              Update Password
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
